import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TextField,
    Box,
    Typography,
    Chip,
    IconButton,
    Tooltip,
} from '@mui/material';
import {
    CheckCircle,
    Payment,
    Schedule,
    Cancel,
    PersonOff,
    PlayArrow,
    EventAvailable,
    Close as CloseIcon,
} from '@mui/icons-material';

interface StatusChange {
    timestamp: string;
    fromStatus: string;
    toStatus: string;
    changedBy: string;
    notes?: string;
}

interface Reservation {
    id: string;
    clientId?: string;
    customerName: string;
    customerEmail: string;
    startDate: string;
    endDate: string;
    status: string;
    statusHistory?: StatusChange[];
    paymentStatus?: 'pending' | 'partial' | 'paid' | 'refunded';
    completionStatus?: 'scheduled' | 'confirmed' | 'in_progress' | 'completed' | 'no_show' | 'cancelled';
    rescheduleCount?: number;
    lastRescheduleDate?: string;
    notes?: string;
}

interface ShiftStatusTrackerProps {
    open: boolean;
    onClose: () => void;
    reservation: Reservation | null;
    onUpdateStatus: (reservationId: string, updates: Partial<Reservation>) => Promise<void>;
}

const completionStatusOptions = [
    { value: 'scheduled', label: 'Programado', icon: <EventAvailable />, color: 'default' },
    { value: 'confirmed', label: 'Confirmado', icon: <CheckCircle />, color: 'success' },
    { value: 'in_progress', label: 'En Progreso', icon: <PlayArrow />, color: 'warning' },
    { value: 'completed', label: 'Completado', icon: <CheckCircle />, color: 'success' },
    { value: 'no_show', label: 'No Asistió', icon: <PersonOff />, color: 'error' },
    { value: 'cancelled', label: 'Cancelado', icon: <Cancel />, color: 'error' },
];

const paymentStatusOptions = [
    { value: 'pending', label: 'Pendiente', color: 'warning' },
    { value: 'partial', label: 'Pago Parcial', color: 'warning' },
    { value: 'paid', label: 'Pagado', color: 'success' },
    { value: 'refunded', label: 'Reembolsado', color: 'info' },
];

const ShiftStatusTracker: React.FC<ShiftStatusTrackerProps> = ({
    open,
    onClose,
    reservation,
    onUpdateStatus,
}) => {
    const [completionStatus, setCompletionStatus] = useState(reservation?.completionStatus || 'scheduled');
    const [paymentStatus, setPaymentStatus] = useState(reservation?.paymentStatus || 'pending');
    const [notes, setNotes] = useState('');
    const [loading, setLoading] = useState(false);

    React.useEffect(() => {
        if (reservation) {
            setCompletionStatus(reservation.completionStatus || 'scheduled');
            setPaymentStatus(reservation.paymentStatus || 'pending');
            setNotes('');
        }
    }, [reservation]);

    const handleSave = async () => {
        if (!reservation) return;

        setLoading(true);
        try {
            const updates: Partial<Reservation> = {
                completionStatus,
                paymentStatus,
            };

            if (notes.trim()) {
                updates.notes = notes.trim();
            }

            // Add to status history
            const statusChange: StatusChange = {
                timestamp: new Date().toISOString(),
                fromStatus: reservation.completionStatus || 'scheduled',
                toStatus: completionStatus,
                changedBy: 'admin', // TODO: Get from auth context
                notes: notes.trim() || undefined,
            };

            let currentHistory: StatusChange[] = [];
            try {
                if (Array.isArray(reservation.statusHistory)) {
                    currentHistory = reservation.statusHistory;
                } else if (typeof reservation.statusHistory === 'string') {
                    currentHistory = JSON.parse(reservation.statusHistory);
                }
            } catch (e) {
                console.error('Error parsing statusHistory for update:', e);
                currentHistory = [];
            }

            updates.statusHistory = [...currentHistory, statusChange];

            await onUpdateStatus(reservation.id, updates);
            onClose();
        } catch (error) {
            console.error('Error updating status:', error);
        } finally {
            setLoading(false);
        }
    };

    const getStatusChip = (status: string, options: any[]) => {
        const option = options.find(opt => opt.value === status);
        return option ? (
            <Chip
                label={option.label}
                color={option.color as any}
                size="small"
                icon={option.icon}
            />
        ) : null;
    };

    if (!reservation) return null;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6">
                    Actualizar Estado del Turno
                </Typography>
                <IconButton onClick={onClose} size="small">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent>
                <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                        {reservation.customerName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {new Date(reservation.startDate).toLocaleDateString('es-ES', {
                            weekday: 'long',
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                        })}
                    </Typography>
                </Box>

                <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                    <Typography variant="body2" color="text.secondary">
                        Estado Actual:
                    </Typography>
                    {getStatusChip(reservation.completionStatus || 'scheduled', completionStatusOptions)}
                    {getStatusChip(reservation.paymentStatus || 'pending', paymentStatusOptions)}
                </Box>

                <FormControl fullWidth sx={{ mb: 3 }}>
                    <InputLabel>Estado del Turno</InputLabel>
                    <Select
                        value={completionStatus}
                        onChange={(e) => setCompletionStatus(e.target.value)}
                        label="Estado del Turno"
                    >
                        {completionStatusOptions.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    {option.icon}
                                    {option.label}
                                </Box>
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <FormControl fullWidth sx={{ mb: 3 }}>
                    <InputLabel>Estado del Pago</InputLabel>
                    <Select
                        value={paymentStatus}
                        onChange={(e) => setPaymentStatus(e.target.value)}
                        label="Estado del Pago"
                    >
                        {paymentStatusOptions.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    {option.label}
                                </Box>
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Notas (opcional)"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Agregar notas sobre el cambio de estado..."
                />

                {(() => {
                    let history: StatusChange[] = [];
                    try {
                        if (Array.isArray(reservation.statusHistory)) {
                            history = reservation.statusHistory;
                        } else if (typeof reservation.statusHistory === 'string') {
                            history = JSON.parse(reservation.statusHistory);
                        }
                    } catch (e) {
                        console.error('Error parsing statusHistory:', e);
                        history = [];
                    }

                    if (history && history.length > 0) {
                        return (
                            <Box sx={{ mt: 3 }}>
                                <Typography variant="subtitle2" gutterBottom>
                                    Historial de Cambios
                                </Typography>
                                <Box sx={{ maxHeight: 150, overflowY: 'auto' }}>
                                    {[...history].reverse().slice(0, 3).map((change, index) => (
                                        <Box key={index} sx={{ py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                                            <Typography variant="caption" color="text.secondary">
                                                {new Date(change.timestamp).toLocaleString('es-ES')}
                                            </Typography>
                                            <Typography variant="body2">
                                                {change.fromStatus} → {change.toStatus}
                                            </Typography>
                                            {change.notes && (
                                                <Typography variant="body2" color="text.secondary">
                                                    "{change.notes}"
                                                </Typography>
                                            )}
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                        );
                    }
                    return null;
                })()}
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose} disabled={loading}>
                    Cancelar
                </Button>
                <Button
                    onClick={handleSave}
                    variant="contained"
                    disabled={loading}
                    sx={{ minWidth: 100 }}
                >
                    {loading ? 'Guardando...' : 'Actualizar'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ShiftStatusTracker;